import React, { useEffect, useState, useCallback } from 'react';
import { Button, TextField, Typography, Select, MenuItem, Box } from '@mui/material';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import './walletDetails.css';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const UserWalletDetails = () => {
    const { id } = useParams();
    const location = useLocation();
    const pickupDate = location.state?.pickupDate || '';
    const [material, setMaterial] = useState(location.state?.material || '');
    const [userName, setUserName] = useState('');
    const [withdrawals, setWithdrawals] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [weightToAdd, setWeightToAdd] = useState('');
    const [statusMap, setStatusMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [walletBalance, setWalletBalance] = useState('');
    const [totalWeightRecycled, setTotalWeightRecycled] = useState('0');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [transactionAmount, setTransactionAmount] = useState('0');
    const [transactionType, setTransactionType] = useState('credit');
    const [scrapRates, setScrapRates] = useState([]);
    const [weights, setWeights] = useState([]);
    const [indvScrapTotals, setIndvScrapTotals] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [userLocation, setUserLocation] = useState(location.state?.pickupAddress || '');
    const [customRates, setCustomRates] = useState([]);
    const [profitPercentage, setProfitPercentage] = useState('');
    const [bill, setBill] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('accessToken'));

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/wallet/mywithdrawalreq/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUserName(response.data.username);
            setWithdrawals(response.data.withdrawals || []);
            setWalletBalance(response.data.walletAmount);
            setTotalWeightRecycled(response.data.totalWeightRecycled || '0');

            const transactionsRes = await axios.get(`${process.env.REACT_APP_API_URL}/user/wallet/transactionhistory/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTransactions(transactionsRes.data.transactions || []);

            const initialStatusMap = {};
            response.data.withdrawals.forEach((withdrawal) => {
                initialStatusMap[withdrawal._id] = withdrawal.status;
            });
            setStatusMap(initialStatusMap);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);


    const fetchScrapRates = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/rate/scraprate`);
            setScrapRates(response.data.scrapRates || []);
            setCustomRates(response.data.scrapRates || []);
        } catch (err) {
            console.error('Failed to fetch scrap rates:', err);
            setError('Failed to fetch scrap rates');
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (isModalOpen) {
            fetchScrapRates();
        }
    }, [isModalOpen, fetchScrapRates]);

    const handleAddWeight = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('accessToken'));
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/wallet/addtotalweight`,
                { id, totalWeightRecycled: weightToAdd },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setWeightToAdd('');
            await fetchData();
        } catch (err) {
            console.error(err);
            setError('Failed to add money and total weight');
        }
    };

    const handleWeightChange = (scrap, rate, value) => {
        const newWeights = { ...weights, [scrap]: parseFloat(value) || 0 };
        setWeights(newWeights);

        const newIndvTotals = { ...indvScrapTotals };
        const total = Object.entries(newWeights).reduce((sum, [key, weight]) => {
            const matchingRate = customRates.find((s) => s.scrapName === key);
            if (!matchingRate) return sum;

            const pricePerKg = parseFloat(
                matchingRate.enteredRate?.match(/\d+(\.\d+)?/g)?.[0] ||
                matchingRate.pricePerKg.match(/\d+(\.\d+)?/g)?.[0] ||
                "0"
            );

            newIndvTotals[key] = weight * pricePerKg;

            return sum + weight * pricePerKg;
        }, 0);

        setTransactionAmount(total);
        setIndvScrapTotals(newIndvTotals);
    };

    const handleNewTransaction = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('accessToken'));
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/wallet/newtransaction`,
                { id, amount: transactionAmount, typeoftransaction: transactionType },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccessMessage('Transaction added successfully');
            setTransactionAmount('');
            setTransactionType('credit');
            setIsModalOpen(false);
            await fetchData();
        } catch (err) {
            console.error(err);
            setError('Failed to add new transaction');
        }
    };

    const handleUpdateStatus = async (withdrawalId, newStatus) => {
        try {
            const token = JSON.parse(sessionStorage.getItem('accessToken'));
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/wallet/updateStatus`,
                { id, withdrawalId, status: newStatus },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setSuccessMessage('Withdrawal status updated successfully');
        } catch (err) {
            console.error(err);
            setError('Failed to update withdrawal status');
        }
    };

    const handleStatusChange = (withdrawalId, newStatus) => {
        setStatusMap((prevStatusMap) => ({ ...prevStatusMap, [withdrawalId]: newStatus }));
    };

    const handleRequestWithdrawal = async () => {
        try {
            const token = JSON.parse(sessionStorage.getItem('accessToken'));
            await axios.post(
                `${process.env.REACT_APP_API_URL}/user/wallet/reqwithdraw/${id}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSuccessMessage('Withdrawal request submitted successfully.');
            await fetchData();
        } catch (err) {
            console.error(err);
            setError('Failed to request withdrawal');
        }
    };

    const handleGeneratePDF = async () => {
        try {
            const requestData = {
                userName,
                pickupDate,
                customRates,
                paymentMethod,
                userLocation,
                scrapRates,
                weights,
                indvScrapTotals,
                transactionAmount,
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/generatepdf`,
                requestData,
                {
                    responseType: "blob",
                }
            );

            const pdfBlob = new Blob([response.data], { type: "application/pdf" });
            const downloadUrl = URL.createObjectURL(pdfBlob);

            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `${userName}_Bill.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Error generating PDF:", err);
        }
    };

    const handleGenerateCert = async () => {
        try {
            const requestData = {
                userName,
                weights,
                material,
                userLocation
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/generatecertificate`,
                requestData,
                {
                    responseType: "blob",
                }
            );

            const pdfBlob = new Blob([response.data], { type: "application/pdf" });
            const downloadUrl = URL.createObjectURL(pdfBlob);

            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `${userName}_Cert.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();

            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Error generating Certificate:", err);
        }
    }

    const handleRateChange = (scrapName, value) => {
        setCustomRates(prevRates =>
            prevRates.map(rate =>
                rate.scrapName === scrapName ? { ...rate, enteredRate: value } : rate
            )
        );
    };

    // const handleGenerateDocuments = async () => {
    //     try {
    //         await handleGeneratePDF();
    //         await handleGenerateCert();
    //     } catch (err) {
    //         console.error("Error generating documents:", err);
    //     }
    // };

    const handleNewDebit = async () => {
        if (!transactionAmount || parseFloat(transactionAmount) <= 0) return;
        const user = JSON.parse(sessionStorage.getItem('user'));

        try {
            const requestData = {
                createdBy: user._id,
                userId: id,
                scrapItems: customRates.map(rate => ({
                    scrapId: rate._id,
                    rate: rate.enteredRate || rate.pricePerKg,
                    weight: weights[rate.scrapName] || 0,
                    total: indvScrapTotals[rate.scrapName] || 0,
                })),
                totalAmount: transactionAmount,
                paymentMethod,
                address: userLocation,
                pickupDate,
                profitPercentage,
                bill
            };

            await axios.post(`${process.env.REACT_APP_API_URL}/save/saveform`, requestData);

            setIsModalOpen(false);
        } catch (error) {
            console.error("Error saving transaction:", error)
        }
    }

    if (loading) return <Typography>Loading...</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <div className="wallet-container">
            <Typography variant="h4" gutterBottom className="wallet-header">
                Wallet Details for {userName || 'User'}
            </Typography>

            <Typography variant="h6" className="current-balance">
                Current Wallet Balance: Rs. {walletBalance} and Total weight recycled: {totalWeightRecycled}kg
            </Typography>
            <div className="main-content">
                <div className="left-section">
                    <div className="add-money-section">
                        {successMessage && <Typography className="success-message">{successMessage}</Typography>}
                        <Typography variant="h6">Add Weight Recycled</Typography>
                        <div className="add-weight-container">
                            <TextField
                                placeholder="Weight"
                                value={weightToAdd}
                                onChange={(e) => setWeightToAdd(e.target.value)}
                                type="number"
                                fullWidth
                                margin="normal"
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddWeight}
                                className="add-money-button"
                                disabled={!weightToAdd}
                            >
                                Add
                            </Button>
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setIsModalOpen(true)}
                                className="new-transaction-button"
                            >
                                New Transaction
                            </Button>
                        </div>
                    </div>

                    <div className="transactions-section">
                        <Typography variant="h6">User Transactions</Typography>
                        <div className="transactions-list">
                            {transactions.length === 0 ? (
                                <Typography>No transactions found</Typography>
                            ) : (
                                [...transactions].reverse().map((transaction) => (
                                    <div key={transaction._id} className="transaction-card">
                                        <Typography
                                            style={{
                                                fontSize: '1.5rem',
                                                color: transaction.typeoftransaction === 'credit' ? 'green' : 'red',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {transaction.typeoftransaction === 'credit' ? '+' : '-'} Rs. {transaction.amount}
                                        </Typography>
                                        <Typography style={{ marginTop: '5px', color: '#555' }}>
                                            Date: {new Date(transaction.date).toLocaleDateString()}
                                        </Typography>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                <div className="right-section">
                    <div className="withdrawal-header">
                        <Typography variant="h6">User Withdrawal Requests</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleRequestWithdrawal}
                            className="withdrawal-btn"
                        >
                            Request Withdrawal For the User
                        </Button>
                    </div>
                    <div className="withdrawal-item-container">
                        {withdrawals.length === 0 ? (
                            <Typography>No withdrawal requests found</Typography>
                        ) : (
                            [...withdrawals].reverse().map((withdrawal) => (
                                <div key={withdrawal._id} className="withdrawal-item">
                                    <Typography>
                                        Amount: Rs. {withdrawal.amount} | Status: {withdrawal.status} | Requested on:{' '}
                                        {new Date(withdrawal.withdrawReqDate).toLocaleDateString()}
                                    </Typography>
                                    <Select
                                        value={statusMap[withdrawal._id] || ''}
                                        onChange={(e) => handleStatusChange(withdrawal._id, e.target.value)}
                                        displayEmpty
                                        className="select-status"
                                    >
                                        <MenuItem value="">Select Status</MenuItem>
                                        <MenuItem value="pending">Pending</MenuItem>
                                        <MenuItem value="completed">Completed</MenuItem>
                                    </Select>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() => handleUpdateStatus(withdrawal._id, statusMap[withdrawal._id])}
                                        className="update-status-btn"
                                    >
                                        Update Status
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {isModalOpen && (
                <div className="modal">
                    <div className="modal-content">
                        <Typography variant="h6">Add New Transaction</Typography>
                        <Select
                            value={transactionType}
                            onChange={(e) => setTransactionType(e.target.value)}
                            fullWidth
                            margin="normal"
                        >
                            <MenuItem value="credit">Credit</MenuItem>
                            <MenuItem value="debit">Debit</MenuItem>
                        </Select>

                        {transactionType === "debit" && scrapRates.length > 0 && (
                            <div>
                                <Typography variant="h6">Scrap Rates</Typography>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Scrap Name</TableCell>
                                                <TableCell>Standard Rates</TableCell>
                                                <TableCell>Rate</TableCell>
                                                <TableCell>Weight</TableCell>
                                                <TableCell>Total</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {scrapRates.map((item) => (
                                                <TableRow key={item._id}>
                                                    <TableCell>{item.scrapName}</TableCell>
                                                    <TableCell>{item.pricePerKg}</TableCell>
                                                    <TableCell>
                                                        <input
                                                            type="text"
                                                            value={customRates.find(rate => rate.scrapName === item.scrapName)?.enteredRate || ""}
                                                            onChange={(e) => handleRateChange(item.scrapName, e.target.value)}
                                                            placeholder="Enter rate"
                                                        />
                                                    </TableCell>


                                                    <TableCell>
                                                        <input
                                                            type="number"
                                                            value={weights[item.scrapName] || ""}
                                                            onChange={(e) =>
                                                                handleWeightChange(item.scrapName, item.pricePerKg, e.target.value)
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {(indvScrapTotals[item.scrapName] || 0).toFixed(2)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell colSpan={12}>
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        alignItems="flex-end"
                                                        gap={1}
                                                        paddingRight={3}
                                                    >
                                                        <Typography variant="h6">Grand Total: Rs. {transactionAmount || 0}</Typography>
                                                        {/* <Typography variant="h7">Platform Charge Percentage: {profitPercentage || 0}%</Typography> */}
                                                        {/* <Typography variant="h7">Customer bill: Rs. {bill || 0}</Typography> */}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <div style={{ marginTop: "20px", paddingTop: "10px", borderTop: "1px solid #ddd" }}>
                                    <Typography variant="h6">Payment Method</Typography>
                                    <div style={{ marginBottom: "10px" }}>
                                        <label>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="Cash"
                                                checked={paymentMethod === "Cash"}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                            />{" "}
                                            Cash
                                        </label>
                                        <label style={{ marginLeft: "20px" }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="UPI"
                                                checked={paymentMethod === "UPI"}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                            />{" "}
                                            UPI
                                        </label>
                                        <label style={{ marginLeft: "20px" }}>
                                            <input
                                                type="radio"
                                                name="paymentMethod"
                                                value="Bank Transfer"
                                                checked={paymentMethod === "Bank Transfer"}
                                                onChange={(e) => setPaymentMethod(e.target.value)}
                                            />{" "}
                                            Bank Transfer
                                        </label>
                                    </div>

                                    {/* <TextField
                                        label="Profit Percentage"
                                        value={profitPercentage}
                                        onChange={(e) => setProfitPercentage(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    /> */}

                                    <TextField
                                        label="User Location"
                                        value={userLocation}
                                        onChange={(e) => setUserLocation(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    />
                                    <TextField
                                        label="Material Description"
                                        value={material}
                                        onChange={(e) => setMaterial(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                    />
                                </div>
                                <Button
                                    variant="contained"
                                    color="default"
                                    onClick={handleGenerateCert}
                                    disabled={scrapRates.length === 0}
                                >
                                    Download Certificate
                                </Button>
                            </div>
                        )}

                        {transactionType === "credit" && (

                            <TextField
                                label="Amount"
                                value={transactionAmount}
                                onChange={(e) => setTransactionAmount(e.target.value)}
                                type="number"
                                fullWidth
                                margin="normal"
                            />
                        )}
                        <div className="modal-actions">
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => setIsModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    handleNewTransaction();
                                    handleNewDebit();
                                    handleGeneratePDF();
                                    handleGenerateCert();
                                }}

                                disabled={!transactionAmount || parseFloat(transactionAmount) <= 0}
                            >
                                Submit
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserWalletDetails;
